import {
  asNonNegativeNumberString,
  asPositiveIntegerString,
} from "../utils/nonNegativeInput";
import { DEFAULT_DISPLAY_CURRENCY } from "../constants/currency";
import { getPlatformCurrency, normalizeCurrency } from "./currency";

const asString = (value, fallback = "") =>
  value === undefined || value === null ? fallback : String(value);

const asPlatform = (value) => asString(value).trim();
const asAlias = (value) => asString(value).trim();

const makeLegacyAccountId = (name, alias = "") =>
  `legacy:${name}${alias ? `:${alias}` : ""}`;

const asId = (value, fallback) => {
  if (value === undefined || value === null || value === "") return fallback;
  const n = Number(value);
  return Number.isFinite(n) && String(value).trim() !== "" ? n : String(value);
};

const asIsoDate = (value) => {
  const date = value ? new Date(value) : new Date();
  return Number.isNaN(date.getTime()) ? new Date().toISOString() : date.toISOString();
};

export const normalizeTournament = (tournament, index = 0) => {
  const plataforma = asPlatform(tournament?.plataforma || tournament?.platform);
  const naturalCurrency = getPlatformCurrency(plataforma);

  return {
    id: asId(tournament?.id, Date.now() + index),
    source: tournament?.source === "common" ? "common" : "user",
    nome: asString(tournament?.nome).trim(),
    buyin: asNonNegativeNumberString(tournament?.buyin, "0"),
    buyinCurrency: normalizeCurrency(
      tournament?.buyinCurrency || tournament?.buy_in_currency,
      naturalCurrency
    ),
    plataforma,
    modalidade: asString(tournament?.modalidade, "Vanilla"),
    players: asNonNegativeNumberString(tournament?.players),
    valorTicket: asNonNegativeNumberString(tournament?.valorTicket),
    ticketValueCurrency: normalizeCurrency(
      tournament?.ticketValueCurrency || tournament?.ticket_value_currency,
      naturalCurrency
    ),
    criadoEm: asIsoDate(tournament?.criadoEm),
  };
};

export const normalizeRegistro = (registro, index = 0) => ({
  id: asId(registro?.id, Date.now() + index),
  torneioId: asId(registro?.torneioId, 0),
  plataforma: asPlatform(registro?.plataforma || registro?.platform),
  inscricoes: asPositiveIntegerString(registro?.inscricoes),
  premiacao: asNonNegativeNumberString(registro?.premiacao),
  prizeCurrency: normalizeCurrency(
    registro?.prizeCurrency || registro?.prize_currency,
    ""
  ),
  tickets: asNonNegativeNumberString(registro?.tickets),
  isSatellite: Boolean(registro?.isSatellite),
  registradoEm: asIsoDate(registro?.registradoEm),
  deletedAt: registro?.deletedAt || registro?.deleted_at || registro?.DeletedAt || null,
});

export const normalizeTournaments = (tournaments) =>
  Array.isArray(tournaments)
    ? tournaments.map(normalizeTournament).filter((t) => t.nome && Number(t.buyin) > 0)
    : [];

export const normalizeRegistros = (registros) =>
  Array.isArray(registros)
    ? registros.map(normalizeRegistro).filter((r) => r.torneioId)
    : [];

const normalizeSimplifiedAccount = (account, index = 0) => {
  const name = asPlatform(account?.name || account?.platform || account?.plataforma);
  const alias = asAlias(account?.alias);
  const naturalCurrency = getPlatformCurrency(name);
  return {
    id: asString(account?.id || account?.platform_id, makeLegacyAccountId(name, alias || index)),
    name,
    alias,
    balance: asNonNegativeNumberString(account?.balance ?? account?.value),
    balanceCurrency: normalizeCurrency(
      account?.balanceCurrency || account?.balance_currency || account?.currency,
      naturalCurrency
    ),
    withdrawn: asNonNegativeNumberString(account?.withdrawn),
    withdrawnCurrency: normalizeCurrency(
      account?.withdrawnCurrency || account?.withdrawn_currency || account?.currency,
      naturalCurrency
    ),
    deposited: asNonNegativeNumberString(account?.deposited),
    depositedCurrency: normalizeCurrency(
      account?.depositedCurrency || account?.deposited_currency || account?.currency,
      naturalCurrency
    ),
  };
};

const normalizeSimplifiedAccounts = (bankroll) => {
  if (Array.isArray(bankroll?.accounts)) {
    return bankroll.accounts
      .map(normalizeSimplifiedAccount)
      .filter((account) => account.name);
  }

  if (!bankroll?.platforms || typeof bankroll.platforms !== "object") return [];

  return Object.entries(bankroll.platforms)
    .map(([platform, value]) => {
      const name = asPlatform(platform);
      return normalizeSimplifiedAccount({
        id: makeLegacyAccountId(name),
        name,
        alias: "",
        balance: value,
        withdrawn: bankroll?.withdrawals?.[platform],
        deposited: bankroll?.deposits?.[platform],
      });
    })
    .filter((account) => account.name);
};

export const normalizeSimplifiedBankroll = (bankroll) => ({
  base: asNonNegativeNumberString(bankroll?.base),
  baseCurrency: normalizeCurrency(
    bankroll?.baseCurrency || bankroll?.base_currency,
    DEFAULT_DISPLAY_CURRENCY
  ),
  accounts: normalizeSimplifiedAccounts(bankroll),
  platforms:
    bankroll?.platforms && typeof bankroll.platforms === "object"
      ? Object.fromEntries(
          Object.entries(bankroll.platforms).map(([platform, value]) => [
            platform,
            asNonNegativeNumberString(value),
          ])
        )
      : {},
  withdrawals:
    bankroll?.withdrawals && typeof bankroll.withdrawals === "object"
      ? Object.fromEntries(
          Object.entries(bankroll.withdrawals).map(([platform, value]) => [
            platform,
            asNonNegativeNumberString(value),
          ])
        )
      : {},
  deposits:
    bankroll?.deposits && typeof bankroll.deposits === "object"
      ? Object.fromEntries(
          Object.entries(bankroll.deposits).map(([platform, value]) => [
            platform,
            asNonNegativeNumberString(value),
          ])
        )
      : {},
  history: Array.isArray(bankroll?.history)
    ? bankroll.history
        .filter((entry) => entry?.date)
        .map((entry) => ({
          date: asString(entry.date),
          base: asNonNegativeNumberString(entry.base),
          baseCurrency: normalizeCurrency(
            entry.baseCurrency || entry.base_currency,
            DEFAULT_DISPLAY_CURRENCY
          ),
          adjustedTotal:
            entry.adjustedTotal === undefined && entry.adjusted_total === undefined
              ? undefined
              : asNonNegativeNumberString(
                  entry.adjustedTotal ?? entry.adjusted_total
                ),
          adjustedTotalCurrency: normalizeCurrency(
            entry.adjustedTotalCurrency || entry.adjusted_total_currency,
            DEFAULT_DISPLAY_CURRENCY
          ),
          accounts: normalizeSimplifiedAccounts(entry),
          platforms:
            entry.platforms && typeof entry.platforms === "object"
              ? Object.fromEntries(
                  Object.entries(entry.platforms).map(([platform, value]) => [
                    platform,
                    asNonNegativeNumberString(value),
                  ])
                )
              : {},
          withdrawals:
            entry.withdrawals && typeof entry.withdrawals === "object"
              ? Object.fromEntries(
                  Object.entries(entry.withdrawals).map(([platform, value]) => [
                    platform,
                    asNonNegativeNumberString(value),
                  ])
                )
              : {},
          deposits:
            entry.deposits && typeof entry.deposits === "object"
              ? Object.fromEntries(
                  Object.entries(entry.deposits).map(([platform, value]) => [
                    platform,
                    asNonNegativeNumberString(value),
                  ])
                )
              : {},
        }))
    : [],
});
